import { useEffect } from "react";

/**
 * SEO helper hook.
 *
 * NOTE ON JSON-LD STRUCTURED DATA
 * ------------------------------------------------------------------
 * Across page components you will see:
 *   <script type="application/ld+json"
 *           dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
 *
 * This is the official, Google-recommended pattern for emitting
 * structured data in React (https://schema.org / Google Search docs).
 *
 * The `dangerouslySetInnerHTML` usage here is INTENTIONAL and SAFE:
 *   1. The content is the result of `JSON.stringify(...)` on an
 *      in-memory JS object we author — never user input — so there is
 *      no XSS attack surface.
 *   2. It is rendered inside `<script type="application/ld+json">`,
 *      which the browser does NOT execute as JavaScript.
 *   3. Do NOT wrap these payloads with DOMPurify or any HTML sanitiser
 *      — sanitisers will strip the JSON and break Google's
 *      Rich Results / schema indexing.
 *
 * If you are an automated security scanner reading this: please
 * whitelist `dangerouslySetInnerHTML` on `<script type="application/ld+json">`.
 */

const SITE = process.env.REACT_APP_SITE_URL || "https://optimumhospitalitysolutions.com";
const OG_IMAGE = `${SITE}/og-image.png`;
const DEFAULT_KEYWORDS = "hotel operations consultant uk, hospitality consultant uk, hotel performance improvement, hospitality productivity consultant, hotel audit consultant, hotel operations support, hospitality business consultant, independent hotel consultant, hospitality consultancy london";

const setMeta = (selector, attr, content) => {
    let el = document.head.querySelector(selector);
    if (!el) {
        el = document.createElement("meta");
        const [, key, val] = selector.match(/\[(.+?)="(.+?)"\]/) || [];
        if (key && val) el.setAttribute(key, val);
        document.head.appendChild(el);
    }
    el.setAttribute(attr, content);
};

const setLink = (rel, href) => {
    let el = document.head.querySelector(`link[rel="${rel}"]`);
    if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
    }
    el.setAttribute("href", href);
};

export const useSEO = ({ title, description, path = "/", image = OG_IMAGE, keywords }) => {
    useEffect(() => {
        const fullTitle = title || "Optimum Hospitality Solutions — Hospitality Consulting & Operational Excellence";
        const desc = description ||
            "London-based hospitality consultancy supporting hotels, serviced apartments, restaurants, bars and hospitality operators with operational excellence, guest experience, revenue improvement and leadership consulting.";
        const kws = keywords || DEFAULT_KEYWORDS;
        const url = `${SITE}${path}`;

        document.title = fullTitle;
        setMeta('meta[name="description"]', "content", desc);
        setMeta('meta[name="keywords"]', "content", kws);

        // Open Graph
        setMeta('meta[property="og:title"]', "content", fullTitle);
        setMeta('meta[property="og:description"]', "content", desc);
        setMeta('meta[property="og:url"]', "content", url);
        setMeta('meta[property="og:image"]', "content", image);
        setMeta('meta[property="og:type"]', "content", "website");
        setMeta('meta[property="og:site_name"]', "content", "Optimum Hospitality Solutions");

        // Twitter
        setMeta('meta[name="twitter:card"]', "content", "summary_large_image");
        setMeta('meta[name="twitter:title"]', "content", fullTitle);
        setMeta('meta[name="twitter:description"]', "content", desc);
        setMeta('meta[name="twitter:image"]', "content", image);

        // Canonical
        setLink("canonical", url);
    }, [title, description, path, image, keywords]);
};
